import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

import { N0DES_API_PREFIX } from "@/api/domain"
import { connectWs } from "@/api/ws"
import type { ProjectIdParam, Uuid } from "@/types"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NetDiagnostic {
  id: Uuid
  project_id: Uuid
  endpoint_id: string
  report: DiagnosticsReport
  created_at: string
}

export interface DiagnosticsReport {
  endpoint_id: string
  net_report: NetReport | null
  direct_addrs: string[]
  portmap_probe: PortMapProbe | null
  iroh_version: string
  iroh_n0des_version: string
}

export interface NetReport {
  udp_v4: boolean
  udp_v6: boolean
  global_v4: string | null
  global_v6: string | null
  mapping_varies_by_dest_ipv4: boolean | null
  mapping_varies_by_dest_ipv6: boolean | null
  preferred_relay: string | null
  relay_latency: RelayLatencies
  captive_portal: boolean | null
}

/** Duration values from serde: BTreeMap<RelayUrl, Duration> where Duration = {secs, nanos} */
export interface RelayLatencies {
  ipv4: Record<string, { secs: number; nanos: number }>
  ipv6: Record<string, { secs: number; nanos: number }>
  https: Record<string, { secs: number; nanos: number }>
}

export interface PortMapProbe {
  upnp: boolean
  pcp: boolean
  nat_pmp: boolean
}

/** Convert a serde Duration ({secs, nanos}) to milliseconds */
export function durationToMs(d: { secs: number; nanos: number }): number {
  return d.secs * 1000 + d.nanos / 1_000_000
}

// ---------------------------------------------------------------------------
// NAT classification (ported from Rust diagnostics example)
// ---------------------------------------------------------------------------

export type NatType =
  | "No NAT"
  | "Endpoint-Independent"
  | "Endpoint-Dependent"
  | "Unknown"

export function classifyNat(report: DiagnosticsReport): NatType {
  const nr = report.net_report
  if (!nr) return "Unknown"
  if (!nr.udp_v4 && !nr.udp_v6) return "Unknown"

  // "No NAT": the endpoint's local address matches its public address.
  const globalV4 = nr.global_v4
  const globalV6 = nr.global_v6
  if (globalV4 || globalV6) {
    const hasDirectMatch = report.direct_addrs.some(
      (addr) => addr === globalV4 || addr === globalV6,
    )
    if (hasDirectMatch) return "No NAT"
  }

  // Match Rust logic: OR both when present, fall back to whichever is available
  const v4 = nr.mapping_varies_by_dest_ipv4
  const v6 = nr.mapping_varies_by_dest_ipv6
  const mappingVariesByDest = v4 != null && v6 != null ? v4 || v6 : (v4 ?? v6)

  if (mappingVariesByDest === false) return "Endpoint-Independent"
  if (mappingVariesByDest === true) return "Endpoint-Dependent"
  return "Unknown"
}

export const natTypeInfo: Record<
  NatType,
  { description: string; badgeClass: string }
> = {
  "No NAT": {
    description:
      "No NAT detected — the endpoint's local address matches its public address. With the correct firewall configuration, no holepunching is required.",
    badgeClass: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200",
  },
  "Endpoint-Independent": {
    description:
      "Endpoint-independent NAT — the most common case for home networks. One outbound UDP packet opens a port for incoming traffic from any address. Holepunching works reliably.",
    badgeClass:
      "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200",
  },
  "Endpoint-Dependent": {
    description:
      "Endpoint-dependent NAT (symmetric) — only the specific address that was sent to is allowed to send traffic back. Direct connections require port prediction. Connections will primarily use relays.",
    badgeClass:
      "bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200",
  },
  Unknown: {
    description:
      "NAT behavior could not be determined from the available data.",
    badgeClass: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
  },
}

/** Per-endpoint online/capability status returned by GET /online-endpoints */
export interface EndpointStatus {
  online: boolean
  has_cap: boolean
}

/** Map of endpoint_id → status */
export type EndpointStatusMap = Record<string, EndpointStatus>

/** Incremental WS message for endpoint activity */
interface EndpointActivityMessage {
  type: "connected" | "disconnected" | "cap_changed"
  endpoints: string[]
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export const netDiagnosticsApi = createApi({
  reducerPath: "netDiagnostics",
  baseQuery: fetchBaseQuery({
    baseUrl: `${N0DES_API_PREFIX}net-diagnostics`,
    mode: "cors",
    credentials: "include",
  }),
  endpoints: (build) => ({
    // -----------------------------------------------------------------------
    // Online endpoints (status map + WS incremental updates)
    // -----------------------------------------------------------------------
    onlineEndpointsWs: build.query<EndpointStatusMap, ProjectIdParam>({
      query: ({ project_id }) => `${project_id}/online-endpoints`,

      async onCacheEntryAdded(
        args,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        await cacheDataLoaded

        // Re-fetch the full status map to pick up has_cap for new endpoints
        const refetchStatus = async () => {
          try {
            const res = await fetch(
              `${N0DES_API_PREFIX}net-diagnostics/${args.project_id}/online-endpoints`,
              { credentials: "include" },
            )
            if (!res.ok) return
            const fresh: EndpointStatusMap = await res.json()
            updateCachedData(() => fresh)
          } catch {
            // best-effort
          }
        }

        const cleanup = connectWs(
          `${N0DES_API_PREFIX}net-diagnostics/${args.project_id}/online-endpoints/ws/`,
          (event: MessageEvent) => {
            const msg: EndpointActivityMessage = JSON.parse(event.data)
            let needsRefetch = false
            updateCachedData((draft) => {
              for (const id of msg.endpoints) {
                if (msg.type === "connected") {
                  if (!(id in draft)) {
                    needsRefetch = true
                  }
                  draft[id] = {
                    online: true,
                    has_cap: draft[id]?.has_cap ?? false,
                  }
                } else if (msg.type === "cap_changed") {
                  needsRefetch = true
                } else if (id in draft) {
                  draft[id] = { ...draft[id], online: false }
                }
              }
            })
            if (needsRefetch) {
              refetchStatus()
            }
          },
        )
        await cacheEntryRemoved
        cleanup()
      },
    }),

    // -----------------------------------------------------------------------
    // Reports list (with WS for new reports)
    // -----------------------------------------------------------------------
    reportsWs: build.query<NetDiagnostic[], ProjectIdParam>({
      query: ({ project_id }) => ({
        url: project_id,
        params: { limit: 50, offset: 0 },
      }),

      async onCacheEntryAdded(
        args,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        await cacheDataLoaded

        const cleanup = connectWs(
          `${N0DES_API_PREFIX}net-diagnostics/${args.project_id}/reports/ws/`,
          (event: MessageEvent) => {
            const nd: NetDiagnostic = JSON.parse(event.data)
            updateCachedData((draft) => {
              // Prepend new report so newest is first
              draft.unshift(nd)
            })
          },
        )
        await cacheEntryRemoved
        cleanup()
      },
    }),

    // -----------------------------------------------------------------------
    // Single report
    // -----------------------------------------------------------------------
    getReport: build.query<NetDiagnostic, ProjectIdParam & { report_id: Uuid }>(
      {
        query: ({ project_id, report_id }) => `${project_id}/${report_id}`,
      },
    ),

    // -----------------------------------------------------------------------
    // Trigger diagnostic on a remote endpoint
    // -----------------------------------------------------------------------
    runDiagnostic: build.mutation<
      NetDiagnostic,
      ProjectIdParam & { endpoint_id: string }
    >({
      query: ({ project_id, endpoint_id }) => ({
        url: `${project_id}/run/${endpoint_id}`,
        method: "POST",
      }),
      async onQueryStarted({ project_id }, { dispatch, queryFulfilled }) {
        try {
          const { data: nd } = await queryFulfilled
          dispatch(
            netDiagnosticsApi.util.updateQueryData(
              "reportsWs",
              { project_id },
              (draft) => {
                draft.unshift(nd)
              },
            ),
          )
        } catch {
          // no-op: WebSocket will deliver it eventually
        }
      },
    }),

    // -----------------------------------------------------------------------
    // Delete a report
    // -----------------------------------------------------------------------
    deleteReport: build.mutation<void, ProjectIdParam & { report_id: Uuid }>({
      query: ({ project_id, report_id }) => ({
        url: `${project_id}/${report_id}`,
        method: "DELETE",
      }),
      async onQueryStarted(
        { project_id, report_id },
        { dispatch, queryFulfilled },
      ) {
        const patch = dispatch(
          netDiagnosticsApi.util.updateQueryData(
            "reportsWs",
            { project_id },
            (draft) => {
              const idx = draft.findIndex((r) => r.id === report_id)
              if (idx !== -1) draft.splice(idx, 1)
            },
          ),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),

    // -----------------------------------------------------------------------
    // Load more reports (appends older reports to the reportsWs cache)
    // -----------------------------------------------------------------------
    loadMoreReports: build.mutation<
      NetDiagnostic[],
      ProjectIdParam & { offset: number }
    >({
      query: ({ project_id, offset }) => ({
        url: project_id,
        params: { limit: 50, offset },
      }),
      async onQueryStarted({ project_id }, { dispatch, queryFulfilled }) {
        try {
          const { data: older } = await queryFulfilled
          dispatch(
            netDiagnosticsApi.util.updateQueryData(
              "reportsWs",
              { project_id },
              (draft) => {
                const existing = new Set(draft.map((r) => r.id))
                for (const r of older) {
                  if (!existing.has(r.id)) draft.push(r)
                }
              },
            ),
          )
        } catch {
          // no-op
        }
      },
    }),
  }),
})

export const {
  useOnlineEndpointsWsQuery,
  useReportsWsQuery,
  useGetReportQuery,
  useRunDiagnosticMutation,
  useDeleteReportMutation,
  useLoadMoreReportsMutation,
} = netDiagnosticsApi
